import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

// Attach JWT from cookie to every request
api.interceptors.request.use((config) => {
  const token = Cookies.get('cxo_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-logout on 401
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && Cookies.get('cxo_token')) {
      Cookies.remove('cxo_token', { path: '/' });
      window.dispatchEvent(new Event('cxo_logout'));
    }
    return Promise.reject(err);
  }
);

// ── Auth ──
export const authAPI = {
  registerSpeaker: (data) => api.post('/auth/register/speaker', data),
  registerAttendee: (data) => api.post('/auth/register/attendee', data),
  verifyOtp: (data) => api.post('/auth/verify-otp', data),
  resendOtp: (email) => api.post('/auth/resend-otp', { email }),
  login: (data) => api.post('/auth/login', data),
  checkEmail: (email) => api.get('/auth/check-email', { params: { email } }),
  me: () => api.get('/auth/me'),
};

// ── Speaker ──
export const speakerAPI = {
  getProfile: () => api.get('/speaker/profile'),
  updateProfile: (data) => api.put('/speaker/profile', data),
  getTalks: () => api.get('/speaker/talks'),
  submitTalk: (data) => api.post('/speaker/talks', data),
  updateTalk: (id, data) => api.put(`/speaker/talks/${id}`, data),
};

// ── Attendee ──
export const attendeeAPI = {
  getProfile: () => api.get('/attendee/profile'),
  updateProfile: (data) => api.put('/attendee/profile', data),
  getRegistrations: () => api.get('/attendee/registrations'),
  registerForTalk: (talkId) => api.post('/attendee/registrations', { talkId }),
  cancelRegistration: (id) => api.delete(`/attendee/registrations/${id}`),
  applySpeaker: (data) => api.post('/attendee/apply-speaker', data),
};

// ── Public ──
export const publicAPI = {
  getSpeakers: () => api.get('/public/speakers'),
  getTalks: () => api.get('/public/talks'),
  getTimeSlots: () => api.get('/public/timeslots'),
  getEvents: () => api.get('/public/events'),
  getEvent: (id) => api.get(`/public/events/${id}`),
  getPrograms: () => api.get('/public/programs'),
  getProgram: (role) => api.get(`/public/programs/${role}`),
  submitNomination: (data) => api.post('/public/nominations', data),
  submitSponsorshipEnquiry: (data) => api.post('/public/sponsorship-enquiry', data),
};

// ── Events (authenticated) ──
export const eventAPI = {
  registerForEvent: (eventId, data) => api.post(`/events/${eventId}/register`, data),
  getMyRegistrations: () => api.get('/events/my-registrations'),
};
// ── Admin ──
export const adminAPI = {
  // Users
  getUsers: () => api.get('/admin/users'),
  getUser: (id) => api.get(`/admin/users/${id}`),
  toggleUser: (id) => api.put(`/admin/users/${id}/toggle`),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  // Speakers
  getSpeakers: () => api.get('/admin/speakers'),
  approveSpeaker: (id) => api.put(`/admin/speakers/${id}/approve`),
  rejectSpeaker: (id) => api.put(`/admin/speakers/${id}/reject`),
  createSpeaker: (data) => api.post('/admin/speakers', data),
  bulkUploadSpeakers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/speakers/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  downloadSampleCSV: () => api.get('/admin/speakers/sample-csv', { responseType: 'blob' }),
  // Talks
  getTalks: () => api.get('/admin/talks'),
  approveTalk: (id) => api.put(`/admin/talks/${id}/approve`),
  rejectTalk: (id) => api.put(`/admin/talks/${id}/reject`),
  // Time Slots
  getTimeSlots: () => api.get('/admin/timeslots'),
  createTimeSlot: (data) => api.post('/admin/timeslots', data),
  updateTimeSlot: (id, data) => api.put(`/admin/timeslots/${id}`, data),
  deleteTimeSlot: (id) => api.delete(`/admin/timeslots/${id}`),
  // Registrations
  getRegistrations: () => api.get('/admin/registrations'),
  // Admin register
  registerAdmin: (data) => api.post('/admin/register', data),
  // Programs
  updateProgram: (roleName, data) => api.put(`/admin/programs/${roleName}`, data),
  // Nominations
  getNominations: () => api.get('/admin/nominations'),
  approveNomination: (id) => api.put(`/admin/nominations/${id}/approve`),
  rejectNomination: (id) => api.put(`/admin/nominations/${id}/reject`),
  // Stats
  getStats: () => api.get('/admin/stats'),
  getDrilldown: () => api.get('/admin/stats/drilldown'),
};

export default api;
